import { experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { Navigate, useSearchParams } from 'react-router-dom';

import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import LandingAiLogo from '@/assets/icons/landing-ai-logo.svg?react';
import IdentifierSignInForm from '@/components/IdentifierSignInForm';
import TextLink from '@/components/TextLink';
import { useSieMethods } from '@/hooks/use-sie';
import PageMeta from '@/shared/components/PageMeta';

import ErrorPage from '../ErrorPage';

import styles from './LandingSignIn.module.scss';
import LandingSocialButtons from './LandingSocialButtons';

const LandingSignIn = () => {
  const { signInMethods, signInMode, signUpMethods, singleSignOnEnabled } = useSieMethods();
  const [params] = useSearchParams();

  const showSso = Boolean(singleSignOnEnabled);

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return (
    <SingleSignOnFormModeContextProvider>
      <div className={styles.page}>
        <PageMeta titleKey="description.sign_in_to_your_account" />

        {/* Header with logo */}
        <div className={styles.header}>
          <LandingAiLogo className={styles.logo} />
        </div>

        {/* Centered form area */}
        <div className={styles.formArea}>
          <div className={styles.formContent}>
            {/* Title */}
            <div className={styles.titleBlock}>
              <span className={styles.titleGray}>Get Started with</span>
              <span className={styles.titleBold}>Agentic Document Extraction</span>
            </div>

            {/* Subtitle */}
            <div className={styles.subtitle}>
              Turn complex, real-world documents into reliable, structured data with accuracy, full
              auditability, and zero training required.
            </div>

            {/* Social + SSO buttons (always visible for LandingAI branding) */}
            <div className={styles.socialSection}>
              <LandingSocialButtons isSingleSignOnEnabled={showSso} />
            </div>

            {/* Divider line between buttons and form */}
            {signInMethods.length > 0 && <div className={styles.dividerLine} />}

            {/* Identifier-first form (always single input, matching production) */}
            {signInMethods.length > 0 && (
              <div className={styles.formSection}>
                <IdentifierSignInForm signInMethods={signInMethods} />
              </div>
            )}

            {/* Create Account link */}
            {signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
              <div className={styles.createAccount}>
                {'No account yet? '}
                <TextLink replace to="/register">
                  Create Account
                </TextLink>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <a
            className={styles.footerLinks}
            href="https://landing.ai/va-tos"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </a>
          <span className={styles.footerSeparator}>|</span>
          <a
            className={styles.footerLinks}
            href="https://landing.ai/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </SingleSignOnFormModeContextProvider>
  );
};

export default LandingSignIn;
